import React, { useState, useEffect } from "react";
import { 
  Building2, 
  MapPin, 
  Bed, 
  BedDouble, 
  User, 
  Users,
  Upload, 
  X,
  ExternalLink
} from "lucide-react";
import api from "../../services/api";
import { BASE_URL } from "../../services/configUrls";

const getOrdinalFloor = (val) => {
  if (val === null || val === undefined) return "";
  const strVal = String(val);
  if (strVal.toLowerCase().includes('floor')) return strVal;
  
  if (strVal.match(/^\d+$/)) {
    const n = parseInt(strVal, 10);
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    const suffix = s[(v - 20) % 10] || s[v] || s[0];
    return `${n}${suffix} Floor`;
  }
  return `${strVal} Floor`;
};

const JaipurMarriottHotel = () => {
  const [showMapModal, setShowMapModal] = useState(false);
  const [allotmentData, setAllotmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllotment = async () => {
      try {
        setLoading(true);
        const response = await api.get(`${BASE_URL}/awards/get-room-allotment`);
        setAllotmentData(response.data);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError("not_found");
        } else {
          setError("error");
          console.error("Error fetching room allotment:", err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAllotment();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-[32px] shadow-sm p-12 min-h-[300px] flex items-center justify-center relative w-full max-w-6xl mx-auto my-4 border border-gray-100">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium text-sm animate-pulse">Loading accommodation details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[32px] shadow-sm p-8 relative max-w-6xl mx-auto my-4 border border-gray-100">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="text-blue-600">
          <BedDouble className="w-8 h-8" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 tracking-tight">Venue & Accommodation</h2>
          <p className="text-sm text-gray-500 font-medium">
            Your accommodation details are shown below.
          </p>
        </div>
      </div>

      <div className="flex flex-col 2xl:flex-row p-1 relative items-stretch gap-4 2xl:gap-0 mt-2">
        {/* Left Card - Hotel Details */}
        <div className="flex-1 bg-gray-50 2xl:bg-gradient-to-r 2xl:from-gray-50/80 2xl:to-transparent 2xl:rounded-l-2xl rounded-2xl 2xl:rounded-r-none border border-gray-100 2xl:border-transparent 2xl:border-none shadow-sm 2xl:shadow-none p-4 flex flex-col sm:flex-row gap-6">
          <div className="relative w-full sm:w-[220px] h-48 sm:h-36 flex-shrink-0">
            <img 
              src="https://media-cdn.tripadvisor.com/media/photo-s/08/86/8a/0f/the-lalit-jaipur.jpg" 
              alt="Hotel exterior" 
              className="w-full h-full object-cover rounded-2xl shadow-sm"
            />
          </div>
          
          <div className="flex flex-col justify-center space-y-6 flex-1 py-1 px-2">
            <div className="flex items-center gap-4">
              <Building2 className="w-5 h-5 text-slate-500 shrink-0" />
              <div className="leading-tight flex flex-col items-start text-left">
                <p className="text-xs text-slate-500 mb-0.5 font-medium">Hotel Name</p>
                <p className="text-sm font-bold text-slate-800">Jaipur Marriott Hotel</p>
              </div>
            </div>
            
            <div className="flex flex-col items-start justify-center">
              <div className="flex items-center gap-4 mb-2">
                <MapPin className="w-5 h-5 text-slate-500 shrink-0" />
                <div className="leading-tight flex flex-col items-start text-left">
                  <p className="text-xs text-slate-500 mb-0.5 font-medium">Location</p>
                  <p className="text-sm font-bold text-slate-800">Ashram Marg, Jaipur</p>
                </div>
              </div>
              
              <button 
                onClick={() => setShowMapModal(true)}
                className="ml-9 text-blue-600 hover:bg-blue-50 text-[11px] font-bold bg-white px-3 py-1.5 rounded border border-blue-200 transition-colors uppercase tracking-wider cursor-pointer shadow-sm flex items-center gap-1.5"
              >
                VIEW MAP <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Divider for desktop */}
        {(!allotmentData || allotmentData.allotment !== false) && error !== "not_found" && (
          <div className="hidden 2xl:block w-px bg-gray-200 shadow-[0_0_1px_rgba(0,0,0,0.1)] my-2"></div>
        )}

        {/* Right Card - Room Details */}
        {(!allotmentData || allotmentData.allotment !== false) && error !== "not_found" && (
        <div className="flex-1 bg-amber-50/80 2xl:bg-[#F9F7E8]/50 2xl:rounded-r-2xl rounded-2xl 2xl:rounded-l-none border border-amber-100/60 2xl:border-transparent 2xl:border-none shadow-sm 2xl:shadow-none p-4 flex flex-col sm:flex-row gap-6">
          <div className="relative w-full sm:w-[220px] h-48 sm:h-36 flex-shrink-0">
            <img 
              src="https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="Room interior" 
              className="w-full h-full object-cover rounded-2xl shadow-sm"
            />
          </div>
          
          <div className="flex flex-col justify-center space-y-4 flex-1 py-1 px-2 min-h-[120px]">
            {!allotmentData || allotmentData.room_no === null ? (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-2 py-4">
                <Bed className="w-8 h-8 text-slate-400/70 mb-1" />
                <div className="leading-tight">
                  <p className="text-sm font-bold text-slate-800">Room Details Not Available</p>
                  <p className="text-[11px] text-slate-500 mt-1">Your room allotment will be available soon.</p>
                </div>
                <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-amber-100/50 border border-amber-200/60">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse mr-1.5"></div>
                  <span className="text-[10px] text-amber-700 font-bold uppercase tracking-wider">Available Soon</span>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4">
                  <BedDouble className="w-5 h-5 text-slate-500 shrink-0" />
                  <div className="leading-tight">
                    <p className="text-xs text-slate-500 mb-0.5 font-medium">Room Number</p>
                    <p className="text-sm font-bold text-slate-800">{allotmentData.room_no}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {allotmentData.is_sharing ? (
                    <Users className="w-5 h-5 text-slate-500 shrink-0" />
                  ) : (
                    <User className="w-5 h-5 text-slate-500 shrink-0" />
                  )}
                  <div className="leading-tight">
                    <p className="text-xs text-slate-500 mb-0.5 font-medium">Room Type</p>
                    <p className="text-sm font-bold text-slate-800">
                      {allotmentData.is_sharing ? "Twin Sharing" : "Single Room"}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <Building2 className="w-5 h-5 text-slate-500 shrink-0" />
                  <div className="leading-tight">
                    <p className="text-xs text-slate-500 mb-0.5 font-medium">Floor</p>
                    <p className="text-sm font-bold text-slate-800">
                      {getOrdinalFloor(allotmentData.floor_no)}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {/* Share button */}
        {/* <button className="absolute -bottom-2 lg:-bottom-2 lg:-right-4 right-0 w-14 h-14 bg-gray-400/30 backdrop-blur hover:bg-gray-400/50 rounded-full flex items-center justify-center text-gray-700 transition-colors shadow-sm cursor-pointer border border-white/40">
          <Upload className="w-6 h-6" />
        </button> */}
      </div>

      {/* Map Modal Overlay */}
      {showMapModal && (
        <div className="fixed inset-0 z-[60000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl overflow-hidden animate-in fade-in duration-200">
            <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50/80">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-500" />
                Hotel Location Map
              </h3>
              <button 
                onClick={() => setShowMapModal(false)}
                className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full text-gray-600 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="w-full h-[60vh] bg-gray-100">
              <iframe
                src="https://maps.google.com/maps?q=Jaipur%20Marriott%20Hotel&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Jaipur Marriott Hotel Location"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JaipurMarriottHotel;
